/**
 * 🎨 BrandedAuthLayout — 2026-05-19
 *
 * Mise en page partagée pour les écrans d'authentification de la plateforme
 * (LoginPage, KioskEntrepot, etc.). Reprend exactement le visuel de la page
 * de connexion : panneau gauche stucco beige avec branding + panneau droit
 * navy avec l'icône PRQC et le formulaire passé en `children`.
 *
 * Props :
 *   - title        : Titre du panneau droit (ex: "Connexion", "Mode Kiosque Entrepôt")
 *   - subtitle     : Texte sous le titre
 *   - children     : Contenu du formulaire (champs + bouton submit)
 *   - footerNote   : Texte en bas du panneau droit (sous le formulaire)
 */
import { useState, useEffect } from 'react';
import { ClipboardList, HardHat, Users, Smartphone, DollarSign, BarChart3 } from 'lucide-react';
import { LOGO_ICONE_BASE64 } from '../pages/LoginPage';

const BACKEND_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const LOGO_COMPLET = `${BACKEND_URL}/api/entreprise/logos/09ad526a-6f56-40fc-87e2-3e22ae048e44`;
const LOGO_ICONE = LOGO_ICONE_BASE64;
const LEFT_BG = {
  backgroundColor: '#d1cfcd',
  backgroundImage: [
    'repeating-linear-gradient(135deg, rgba(255,255,255,0.09) 0px, rgba(255,255,255,0.09) 1px, transparent 1px, transparent 7px)',
    'repeating-linear-gradient(45deg, rgba(0,0,0,0.05) 0px, rgba(0,0,0,0.05) 1px, transparent 1px, transparent 7px)',
  ].join(', '),
};

const DARK_NAVY = '#0c1628';

const FEATURES = [
  { icon: ClipboardList, label: 'CRM & Dossiers', desc: 'Clients, soumissions, suivi projets' },
  { icon: HardHat,       label: 'Production',     desc: 'Bons de travail, équipes, chantier' },
  { icon: Users,         label: 'RH & Équipe',    desc: 'Employés, pointage, formations, congés' },
  { icon: Smartphone,    label: 'SMS & Alertes',  desc: 'Notifications auto & rappels' },
  { icon: DollarSign,    label: 'Facturation',    desc: 'Factures, paiements, dépôts' },
  { icon: BarChart3,     label: 'Rapports',       desc: 'Statistiques & performances' },
];

export default function BrandedAuthLayout({ title, subtitle, children, footerNote, dataTestid }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <div
      className="min-h-screen flex items-stretch"
      data-testid={dataTestid || 'branded-auth-layout'}
      style={{ background: DARK_NAVY }}
    >
      {/* ─── PANNEAU GAUCHE — Texture stucco ─── */}
      <div
        className="hidden lg:flex flex-1 flex-col justify-between p-10 xl:p-14"
        style={{
          ...LEFT_BG,
          borderRight: '1px solid rgba(0,0,0,0.15)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', boxShadow: 'inset 0 0 100px rgba(0,0,0,0.10)' }} />

        {/* Haut : Logo + Plateforme de gestion */}
        <div
          className="relative z-10"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <img
            src={LOGO_COMPLET}
            alt="Peinture Résidentielle QC"
            style={{
              height: '90px',
              width: 'auto',
              display: 'block',
              marginLeft: '-14px',
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.12))',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <p
            style={{
              marginTop: '2px',
              fontSize: '1.25rem',
              fontWeight: 700,
              color: DARK_NAVY,
              letterSpacing: '0.01em',
            }}
          >
            Plateforme de gestion
          </p>
        </div>

        {/* Centre : Texte principal */}
        <div
          className="relative z-10"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(16px)',
            transition: 'opacity 0.8s ease 0.15s, transform 0.8s ease 0.15s',
          }}
        >
          <p
            style={{
              fontSize: 'clamp(2rem, 3.2vw, 2.8rem)',
              fontWeight: 900,
              color: DARK_NAVY,
              lineHeight: 1,
              marginBottom: '20px',
              letterSpacing: '-0.02em',
            }}
          >
            Bienvenue !
          </p>
          <p style={{ fontSize: '1.15rem', fontWeight: 500, color: DARK_NAVY, marginBottom: '6px' }}>
            Une app de
          </p>
          <p
            style={{
              fontSize: 'clamp(1.8rem, 3.2vw, 2.8rem)',
              fontWeight: 900,
              color: DARK_NAVY,
              lineHeight: 1.1,
              marginBottom: '14px',
              letterSpacing: '-0.01em',
            }}
          >
            Peinture Résidentielle Qc
          </p>
          <p style={{ fontSize: '1.15rem', fontWeight: 500, color: DARK_NAVY }}>
            qui regroupe tout au même endroit !
          </p>
        </div>

        {/* Bas : Features */}
        <div
          className="relative z-10"
          style={{ opacity: mounted ? 1 : 0, transition: 'opacity 0.8s ease 0.3s' }}
        >
          <div style={{ height: '3px', background: DARK_NAVY, borderRadius: '2px', marginBottom: '18px' }} />
          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
            {FEATURES.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="flex flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: '#c0192e' }} />
                  <span className="text-xs font-bold" style={{ color: DARK_NAVY }}>{label}</span>
                </div>
                <p className="text-xs leading-snug" style={{ color: DARK_NAVY }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── PANNEAU DROIT — Bannière logo + Formulaire ─── */}
      <div
        className="flex flex-1 lg:max-w-[430px] flex-col justify-center px-7 sm:px-10 py-10"
        style={{ background: DARK_NAVY }}
      >
        {/* Icône PRQC centrée */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(-10px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
            marginBottom: '30px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            src={LOGO_ICONE}
            alt="Peinture Résidentielle QC"
            style={{
              width: '160px',
              height: '160px',
              objectFit: 'contain',
              borderRadius: '24px',
              display: 'block',
              background: 'white',
              padding: '8px',
              boxShadow: '0 6px 30px rgba(0,0,0,0.5)',
            }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        {/* Titre + Sous-titre + Formulaire passé en children */}
        <div
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(14px)',
            transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
          }}
        >
          <h2 className="text-3xl font-bold text-white mb-1">{title}</h2>
          {subtitle && <p className="text-slate-400 mb-7 text-sm">{subtitle}</p>}

          {children}

          {footerNote ? (
            <div className="text-center text-xs text-slate-700 mt-8">{footerNote}</div>
          ) : (
            <p className="text-center text-xs text-slate-700 mt-8">
              Peinture Résidentielle QC &copy; {new Date().getFullYear()}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export { DARK_NAVY };
